import { FieldValues, UseFormSetValue } from 'react-hook-form';
import {
  AssessmentQuestionOption,
  AssessmentsQuestionProps,
  AssessmentsQuestionViewModel,
  TriggerSkipLogic,
} from '../types';
import { isValidAssessmentDate } from './assessmentsAnswersValidation.utils';
import { ASSESSMENTS_PAGE_SIZE } from '../constants';

const checkIfRequiredOptionsSelected = (
  options: AssessmentQuestionOption[],
  selectedOptions: AssessmentQuestionOption[],
): boolean => {
  const requiredOptions = options.filter((opt) => opt.isOptionRequired);
  return requiredOptions.every((reqOpt) =>
    selectedOptions.some((opt) => opt.value === reqOpt.value),
  );
};

type QuestionChecker = (question: AssessmentsQuestionViewModel, formValues: FieldValues) => boolean;

const hasEnteredInvalidDate = (
  question: AssessmentsQuestionViewModel,
  value: FieldValues[string],
): boolean =>
  question.type === 'date' &&
  typeof value === 'string' &&
  value.trim() !== '' &&
  !isValidAssessmentDate(value);

/**
 * Factory that creates a question checker function.
 * @param onlyRequired - If true, non-required questions automatically pass validation
 */
const createQuestionChecker = (onlyRequired: boolean): QuestionChecker => {
  const checker: QuestionChecker = (question, formValues) => {
    const value = formValues[question.id.toString()];

    // An edge case for date quesion, invalid date is considered unanswered
    if (hasEnteredInvalidDate(question, value)) {
      return false;
    }

    // For onlyRequired mode, skip validation for non-required questions
    if (onlyRequired && !question.isRequired) return true;

    switch (question.type) {
      // Informational question types do not require an answer
      case 'display':
        return true;

      case 'label':
        return true;

      // Text-based inputs: answered if non-empty string
      case 'textbox':
      case 'textarea':
      case 'date':
      case 'select':
        return typeof value === 'string' && value.trim() !== '';

      // Checkbox: answered if at least one option selected AND all visible subquestions answered
      case 'checkbox': {
        const isAnswered = Array.isArray(value) && value.length > 0;
        if (!isAnswered) return false;

        const selectedOptions = question.options?.filter((opt) => value.includes(opt.value)) ?? [];
        const requiredOptionsSelected = checkIfRequiredOptionsSelected(
          question.options ?? [],
          selectedOptions,
        );

        if (!requiredOptionsSelected) return false;

        return selectedOptions.every(
          (opt) => opt.subQuestions?.every((subQ) => checker(subQ, formValues)) ?? true,
        );
      }

      // Radio: answered if option selected AND all visible subquestions answered
      case 'radio': {
        const isAnswered = typeof value === 'string' && value.trim() !== '';
        if (!isAnswered) return false;

        const selectedOption = question.options?.find((opt) => opt.value === value);
        // If value doesn't match any option, treat as unanswered (bad data scenario)
        if (!selectedOption) return false;

        const requiredOptionsSelected = checkIfRequiredOptionsSelected(question.options ?? [], [
          selectedOption,
        ]);
        if (!requiredOptionsSelected) return false;

        return selectedOption.subQuestions?.every((subQ) => checker(subQ, formValues)) ?? true;
      }

      default:
        return onlyRequired ? !question.isRequired : false;
    }
  };
  return checker;
};

export const checkIfQuestionIsAnswered = createQuestionChecker(false);
const checkIfRequiredQuestionAndSubQuesionsAreAnswered = createQuestionChecker(true);

const shouldShowNextButton = (
  question: AssessmentsQuestionViewModel,
  fieldValue: string | string[] | undefined,
  isSubQuestion: boolean,
): boolean => {
  if (isSubQuestion) return false;

  switch (question.type) {
    case 'display':
    case 'label':
    case 'textbox':
    case 'textarea':
    case 'date':
    case 'checkbox':
      return true;
    case 'radio':
      return typeof fieldValue === 'string'
        ? (question.options?.find((opt) => opt.value === fieldValue)?.subQuestions?.length ?? 0) > 0
        : false;
    case 'select':
    default:
      return false;
  }
};

type GenerateQuestionPropsType = (props: {
  question: AssessmentsQuestionViewModel;
  fieldValue: string | string[] | undefined;
  setValue: UseFormSetValue<FieldValues>;
  formValues: FieldValues;
  isSubQuestion?: boolean;
  triggerSkipLogic?: TriggerSkipLogic;
}) => AssessmentsQuestionProps;

// Helper to map options with subQuestions
const mapOptionsWithSubQuestions = (
  options: AssessmentQuestionOption[],
  fieldValue: string | string[] | undefined,
  setValue: UseFormSetValue<FieldValues>,
  formValues: FieldValues,
  generateQuestion: GenerateQuestionPropsType,
) =>
  options.map((opt) => {
    const isSelected = Array.isArray(fieldValue)
      ? fieldValue.includes(opt.value)
      : opt.value === fieldValue;

    const subQuestions = isSelected
      ? opt.subQuestions?.map((subQ) =>
          generateQuestion({
            question: subQ,
            fieldValue: formValues[subQ.id.toString()],
            setValue,
            formValues,
            isSubQuestion: true,
          }),
        )
      : undefined;

    return {
      ...opt,
      subQuestions,
    };
  });

// Shared context needed by every answer-change handler.
type AnswerChangeContext = {
  question: AssessmentsQuestionViewModel;
  questionName: string;
  formValues: FieldValues;
  setValue: UseFormSetValue<FieldValues>;
  triggerSkipLogic?: TriggerSkipLogic;
};

/**
 * Persists the new answer to the form and forwards it to the skip logic.
 * `performScroll` controls whether the skip logic auto-scrolls/advances to the
 * resolved target question; each question type sets its own policy below.
 */
const applyAnswerChange = (
  { question, questionName, formValues, setValue, triggerSkipLogic }: AnswerChangeContext,
  changedValue: string | string[],
  performScroll?: boolean,
) => {
  setValue(questionName, changedValue, { shouldDirty: true });
  if (triggerSkipLogic) {
    triggerSkipLogic({
      currentQuestionId: question.id,
      value: changedValue,
      allFormValues: { ...formValues, [questionName]: changedValue },
      performScroll,
    });
  }
};

// Radio: auto-scroll only when the selected option has no subquestions to answer.
const createRadioOnChange =
  (context: AnswerChangeContext, options: AssessmentQuestionOption[]) => (changedValue: string) => {
    const selectedSubQuestions = options.find((opt) => opt.value === changedValue)?.subQuestions;
    applyAnswerChange(context, changedValue, (selectedSubQuestions?.length ?? 0) === 0);
  };

// Checkbox: never auto-scroll — users may select multiple options before moving on.
const createCheckboxOnChange = (context: AnswerChangeContext) => (changedValue: string[]) =>
  applyAnswerChange(context, changedValue);

// Select: do not auto-scroll on selection (FUS-832) — advancing is left to the Next button.
const createSelectOnChange = (context: AnswerChangeContext) => (changedValue: string) =>
  applyAnswerChange(context, changedValue);

// Free-text inputs: defer scrolling to the Next button (performScroll left unset).
const createTextOnChange = (context: AnswerChangeContext) => (changedValue: string) =>
  applyAnswerChange(context, changedValue);

// Helper to map options with children (rendered subquestions)
export const generateQuestionProps: GenerateQuestionPropsType = ({
  question,
  fieldValue,
  setValue,
  formValues,
  isSubQuestion = false,
  triggerSkipLogic,
}) => {
  const questionName = question.id.toString();

  const baseProps = {
    id: question.id,
    name: questionName,
    questionNext: question.questionNext,
    questionNumber: question.questionNumber,
    questionText: question.questionText,
    isRequired: question.isRequired,
    placeholder: question.placeholder,
    optionId: question.optionId,
    showNextButton: shouldShowNextButton(question, fieldValue, isSubQuestion),
    nextButtonDisabled: !checkIfRequiredQuestionAndSubQuesionsAreAnswered(question, formValues),
  };

  const changeContext: AnswerChangeContext = {
    question,
    questionName,
    formValues,
    setValue,
    triggerSkipLogic,
  };

  switch (question.type) {
    case 'radio': {
      const optionsWithSubQuestions = mapOptionsWithSubQuestions(
        question.options,
        fieldValue,
        setValue,
        formValues,
        generateQuestionProps,
      );
      return {
        ...baseProps,
        type: 'radio',
        options: optionsWithSubQuestions,
        selectedOption: question.options.find((opt) => opt.value === fieldValue) ?? {
          value: '',
          label: '',
        },
        onChange: createRadioOnChange(changeContext, question.options),
      };
    }
    case 'checkbox': {
      return {
        ...baseProps,
        type: 'checkbox',
        questionLabelId: `question-${question.id}-label`,
        options: mapOptionsWithSubQuestions(
          question.options,
          fieldValue ?? [],
          setValue,
          formValues,
          generateQuestionProps,
        ),
        selectedOptions: Array.isArray(fieldValue)
          ? question.options.filter((opt) => fieldValue.includes(opt.value))
          : [],
        onChange: createCheckboxOnChange(changeContext),
      };
    }

    case 'select':
      return {
        ...baseProps,
        type: 'select',
        options: question.options,
        value: (fieldValue as string) ?? '',
        onChange: createSelectOnChange(changeContext),
      };

    case 'textbox':
    case 'textarea':
    case 'date':
      return {
        ...baseProps,
        type: question.type,
        value: (fieldValue as string) ?? '',
        onChange: createTextOnChange(changeContext),
      };

    case 'display':
      return {
        ...baseProps,
        type: 'label',
      };

    case 'label':
      return {
        ...baseProps,
        type: 'label',
      };

    default:
      return {
        ...baseProps,
        type: 'unsupported',
      };
  }
};

export const getAssessmentsAnsweredState = (
  questions: AssessmentsQuestionViewModel[],
  formValues: FieldValues,
  skippedQuestionNumbers: number[],
): {
  firstUnanswered: AssessmentsQuestionViewModel | null;
  firstUnskipped: AssessmentsQuestionViewModel | null;
  atleastOneAnswered: boolean;
} => {
  let firstUnanswered: AssessmentsQuestionViewModel | null = null;
  let firstUnskipped: AssessmentsQuestionViewModel | null = null;
  let atleastOneAnswered = false;

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const isSkipped = skippedQuestionNumbers.includes(question.questionNumber);
    const isQuestionAnswered = checkIfQuestionIsAnswered(question, formValues);
    if (!isSkipped && !isQuestionAnswered && firstUnanswered === null) {
      firstUnanswered = question;
    }
    if (!isSkipped && firstUnskipped === null) {
      firstUnskipped = question;
    }
    if (
      !isSkipped &&
      isQuestionAnswered &&
      question.type !== 'display' &&
      question.type !== 'label' &&
      !atleastOneAnswered
    ) {
      atleastOneAnswered = true;
    }
    if (firstUnanswered && atleastOneAnswered) {
      break;
    }
  }

  return {
    firstUnanswered,
    firstUnskipped,
    atleastOneAnswered,
  };
};

export const getAssessmentsAnsweredStateForPage = (
  questions: AssessmentsQuestionViewModel[],
  formValues: FieldValues,
  skippedQuestionNumbers: number[],
  pageNumber: number,
) => {
  const pageQuestions = questions.slice(
    (pageNumber - 1) * ASSESSMENTS_PAGE_SIZE,
    pageNumber * ASSESSMENTS_PAGE_SIZE,
  );

  const answeredState = getAssessmentsAnsweredState(
    pageQuestions,
    formValues,
    skippedQuestionNumbers,
  );

  return answeredState;
};
